import { registerMixpanelSuperProperties, unregisterMixpanelSuperProperty } from './mixpanel'
import { OrderLaunchedFrom } from 'models/purchaseFlow/orderLaunchedFrom'

export const ORDER_ATTEMPT_ID_PROPERTY = 'orderAttemptId'

export interface OrderAttempt {
  orderAttemptId: string
  launchedFrom: OrderLaunchedFrom
}

const mintOrderAttemptId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/** The latest attempt: pending between the click that started it and the flow that adopts it. */
let currentAttempt: { id: string, launchedFrom: OrderLaunchedFrom, adopted: boolean } | null = null

/**
 * Starts an order attempt at the click that opens a purchase flow (ISSUE-3899) and returns its id, which
 * the click's own event must carry explicitly. Not registered as a super property yet: a click whose flow
 * never opens (a dismissed property drawer) would otherwise stamp every later event with it.
 */
export const startOrderAttempt = (launchedFrom: OrderLaunchedFrom): string => {
  const id = mintOrderAttemptId()
  currentAttempt = { id, launchedFrom, adopted: false }
  return id
}

/**
 * Drops a started attempt that no flow adopted, e.g. when the property drawer is dismissed, so a flow opened
 * later from an unrelated CTA mints its own id instead of crediting the abandoned click. A no-op once adopted.
 */
export const discardOrderAttempt = () => {
  if (currentAttempt && !currentAttempt.adopted) currentAttempt = null
}

/**
 * Called by the purchase flow on mount: adopts the attempt its entry click started, or mints one with the surface's
 * fallback entry point for a mount no click started (direct, deep link, Propstack opening the embed), and registers
 * the id as a super property for the attempt's events. A started attempt is adopted once; the next flow is new.
 */
export const adoptOrderAttempt = (fallbackLaunchedFrom: OrderLaunchedFrom): OrderAttempt => {
  const started = currentAttempt && !currentAttempt.adopted ? currentAttempt : null
  const id = started?.id ?? mintOrderAttemptId()
  const launchedFrom = started?.launchedFrom ?? fallbackLaunchedFrom
  currentAttempt = { id, launchedFrom, adopted: true }
  registerMixpanelSuperProperties({ [ORDER_ATTEMPT_ID_PROPERTY]: id })
  return { orderAttemptId: id, launchedFrom }
}

/**
 * Ends the attempt when its flow unmounts, so later events no longer carry the id. A no-op when a newer
 * attempt has taken over: React renders the next flow before it runs the previous one's unmount cleanup.
 */
export const endOrderAttempt = (id: string) => {
  if (currentAttempt?.id !== id) return
  currentAttempt = null
  unregisterMixpanelSuperProperty(ORDER_ATTEMPT_ID_PROPERTY)
}
